import moment from 'moment-timezone';
import AppService from 'App/legacy/AppService';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import {
	type CalendarEvent,
	type HeldSpans,
	type MovableAppointment,
	type Span,
	findAppointmentForEvent,
	getViewDays,
	isMovable,
	isResizable,
	withDragSpans
} from './calendarDrag';
import {
	CalendarDragController,
	type DragGeometry,
	type DragPointer,
	type LivePreview,
	readCalendarGeometry
} from './CalendarDragController';
import { rescheduleAppointment } from './rescheduleAppointment';

export type DragState = { livePreview: LivePreview | null; held: HeldSpans };

type DragProps = {
	view?: string;
	canReschedule?: boolean;
	dayStartHour?: number;
	appointments: MovableAppointment[];
};

type Host = {
	getProps: () => DragProps;
	getState: () => DragState;
	setState: (update: (state: DragState) => Partial<DragState> | null) => void;
	getContainer: () => HTMLElement | null;
	getViewDate: () => Date;
};

type DragPointerDown = DragPointer & { target: Pick<Element, 'closest'>; preventDefault: () => void };

const WRAPPER_CLASS = 'CalendarView__CalendarWrapper';

export class CalendarDragBinding {
	private host: Host;
	private controllers = new Map<'day' | 'week', CalendarDragController>();

	constructor(host: Host) {
		this.host = host;
	}

	isEnabled(): boolean {
		const { canReschedule, view } = this.host.getProps();
		return !!canReschedule && view !== 'month' && hasSoftDeployAccess('FIELD_SALES_V2');
	}

	timezone(): string | undefined {
		return this.host.getProps().view === 'day' ? undefined : window.userTimezone;
	}

	withSpans<T extends { id: number }>(appointments: T[]) {
		const { livePreview, held } = this.host.getState();
		return withDragSpans(appointments, livePreview, held);
	}

	wrapperClassName(): string {
		const mode = this.host.getState().livePreview?.mode;
		if (!mode) {
			return WRAPPER_CLASS;
		}
		return `${WRAPPER_CLASS} ${WRAPPER_CLASS}--${mode === 'resize' ? 'resizing' : 'dragging'}`;
	}

	itemProps(event: CalendarEvent) {
		const appointment = this.isEnabled() ? this.findMovableAppointment(event) : null;
		return {
			onDragPointerDown: appointment ? (e: DragPointerDown) => this.startDrag(appointment, e) : undefined,
			resizable: !!appointment && isResizable(appointment)
		};
	}

	getGeometry = (): DragGeometry | null => {
		const container = this.host.getContainer();
		const { view = '', dayStartHour = 0 } = this.host.getProps();
		if (!container || view === 'month') {
			return null;
		}
		const startHour = view === 'day' ? dayStartHour : 0;
		const geometry = readCalendarGeometry(container, startHour);
		if (!geometry) {
			return null;
		}
		const days = getViewDays(view, this.host.getViewDate(), window.userTimezone);
		return { ...geometry, days, dayStartHour: startHour };
	};

	handlePreview = (livePreview: LivePreview | null) => {
		this.host.setState(() => ({ livePreview }));
	};

	handleDrop = (id: number, span: Span) => {
		this.host.setState(state => ({ livePreview: null, held: { ...state.held, [id]: span } }));
		return rescheduleAppointment(id, span).then(() => this.releaseHeld(id, span));
	};

	handleClickCapture = (e: Pick<Event, 'stopPropagation' | 'preventDefault'>) => {
		const swallowed = Array.from(this.controllers.values()).reduce(
			(consumed, controller) => controller.consumeClick() || consumed,
			false
		);
		if (swallowed) {
			e.stopPropagation();
			e.preventDefault();
		}
	};

	startDrag(appointment: MovableAppointment, e: DragPointerDown) {
		// A meeting whose save is in flight keeps its layout but is not picked up again until it settles.
		if (e.button !== 0 || e.pointerType !== 'mouse' || this.host.getState().held[appointment.id]) {
			return;
		}
		e.preventDefault();
		const mode = e.target.closest('[data-resize-handle]') ? 'resize' : 'move';
		const span = {
			id: appointment.id,
			date: moment(appointment.date).toDate(),
			endDate: moment(appointment.endDate).toDate()
		};
		this.controller().start(span, e, mode, this.host.getContainer());
	}

	destroy() {
		this.controllers.forEach(controller => controller.destroy());
	}

	private controller(): CalendarDragController {
		const key = this.host.getProps().view === 'day' ? 'day' : 'week';
		const existing = this.controllers.get(key);
		if (existing) {
			return existing;
		}
		const created = new CalendarDragController({
			getGeometry: this.getGeometry,
			timezone: this.timezone(),
			onPreview: this.handlePreview,
			onDrop: this.handleDrop
		});
		this.controllers.set(key, created);
		return created;
	}

	private releaseHeld(id: number, span: Span) {
		this.host.setState(state => {
			if (state.held[id] !== span) {
				return null;
			}
			const held = { ...state.held };
			delete held[id];
			return { held };
		});
	}

	private findMovableAppointment(event: CalendarEvent): MovableAppointment | null {
		const found = findAppointmentForEvent(event, this.host.getProps().appointments);
		if (!found) {
			return null;
		}
		const held = this.host.getState().held[found.id];
		const appointment = held ? { ...found, ...held } : found;
		return isMovable(appointment, AppService.getSelf(), this.timezone()) ? appointment : null;
	}
}
